import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';

/* eslint-disable-next-line no-restricted-imports */
import type { StoreSpecial } from '@jane/shared/models';
import type { StoreSpecial as ZStoreSpecial } from '@jane/shared/types';

const isLegacyStoreSpecial = (
  special: StoreSpecial | ZStoreSpecial | V3StoreSpecial
): special is StoreSpecial | ZStoreSpecial => 'enabled' in special;

/**
 * A helper to filter for enabled and supported store specials.
 * hidden_from_row & hidden_from_page are only intended to prevent specials from
 * rendering in the Specials Row & Special Detail Page.
 * use allowHidden = true to let them pass elsewhere
 *
 * v3 specials lack `enabled` and `promo_code` — the v3 backend enforces an
 * active schedule, pickup reservation, and the no-promo-code rule server-side.
 * For v3 inputs we skip the legacy field checks and apply only the
 * special_type whitelist and hidden flags.
 */
export const isSupportedStoreSpecial = (
  special: StoreSpecial | ZStoreSpecial | V3StoreSpecial,
  allowHidden = false
) => {
  const { reservation_modes, special_type, hidden_from_row, hidden_from_page } =
    special;

  const legacyChecksPass = isLegacyStoreSpecial(special)
    ? Boolean(
        special.enabled &&
        /**
         * Jan 12, 2024: Specials are currently configured to be either
         * pickup or delivery. The 'kiosk' reservation mode is vestigial
         * and not modifiable.
         */
        reservation_modes?.['pickup'] &&
        !special.promo_code
      )
    : true;

  const baselineRequirements =
    legacyChecksPass &&
    ['product', 'bundle', 'spending_threshold'].includes(special_type);

  if (allowHidden) {
    return baselineRequirements;
  }
  return baselineRequirements && !hidden_from_row && !hidden_from_page;
};
